import {
  createContext,
  forwardRef,
  useContext,
  type InputHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

import { cn } from '../../lib/utils.js';

interface InputGroupContextValue {
  hasIcon: boolean;
  hasSuffix: boolean;
}

const InputGroupContext = createContext<InputGroupContextValue>({
  hasIcon: false,
  hasSuffix: false,
});

export function useInputGroupContext(): InputGroupContextValue {
  return useContext(InputGroupContext);
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid = false, ...props },
  ref,
) {
  const { hasSuffix } = useInputGroupContext();

  return (
    <input
      ref={ref}
      className={cn('input', invalid && 'is-invalid', hasSuffix && 'input--with-suffix', className)}
      aria-invalid={invalid || undefined}
      {...props}
    />
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, invalid = false, ...props },
  ref,
) {
  return (
    <textarea
      ref={ref}
      className={cn('input', invalid && 'is-invalid', className)}
      aria-invalid={invalid || undefined}
      {...props}
    />
  );
});

export { InputGroupContext };
